//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import HistoryCard, { HistoryItem } from './HistoryCard';
import AiDataService from '../../../utils/AiDataService';
import { useUserAuth } from '../../../UserAuth';
import settings from '../../../Config/settings';
import { cn } from '../../../lib/utils';
import { useTheme } from '../../../ThemeContext';
import type { BackendTurn } from '../../../types/conversationHistory';

interface HistoryTabProps {
  selectedLoopId?: string;
  selectedLoopName?: string | null;
  teamspaceId?: string;
  teamspaceName?: string;
  onHistoryItemClick?: (
    historyId: string,
    title: string,
    messages?: any[],
    taskInfo?: {
      type: 'chat' | 'task';
      taskIcon?: string;
      taskName?: string;
      taskId?: string;
    },
    // The turn-scoped record for this conversation (analyticsai-server PR
    // #409), fetched alongside `messages` above. Always an array — empty
    // when the fetch fails or the conversation predates the backend
    // feature (FR-014); never undefined, so callers don't need an extra
    // fallback branch.
    turns?: BackendTurn[],
  ) => void;
  isBusy?: boolean;
}

const HistoryTab: React.FC<HistoryTabProps> = ({
  selectedLoopId,
  selectedLoopName,
  teamspaceId,
  teamspaceName,
  onHistoryItemClick,
  isBusy = false,
}) => {
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const { user, isAuthenticated } = useUserAuth();
  const [historyItems, setHistoryItems] = React.useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const aiService = useMemo(
    () =>
      new AiDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  useEffect(() => {
    const fetchSessions = async () => {
      if (!selectedLoopId || !teamspaceId || !teamspaceName) {
        setHistoryItems([]);
        return;
      }

      setIsLoading(true);
      try {
        const isDevelopment =
          process.env.NODE_ENV === 'development' ||
          process.env.REACT_APP_ENV === 'development';

        let sessions: any = [];

        if (isDevelopment) {
          const mockHistoryData = await import('./mockHistory.json');
          sessions = mockHistoryData.default || mockHistoryData;
        } else {
          const response = await aiService.getLoopSessions(
            teamspaceId,
            teamspaceName,
            selectedLoopId,
            1,
            20,
            true,
          );
          sessions = response?.sessions || response || [];
        }

        const extractTitleFromMessage = (message: string): string => {
          if (!message) return 'Untitled Session';

          const lines = message.split('\n').filter((line) => line.trim());

          for (const line of lines) {
            const trimmed = line.trim();
            if (trimmed.startsWith('###')) {
              return trimmed.replace(/^#+\s*/, '').substring(0, 100);
            }
            if (trimmed.startsWith('##')) {
              return trimmed.replace(/^#+\s*/, '').substring(0, 100);
            }
            if (trimmed.startsWith('#')) {
              return trimmed.replace(/^#+\s*/, '').substring(0, 100);
            }
            if (
              trimmed &&
              !trimmed.startsWith('-') &&
              !trimmed.startsWith('*') &&
              trimmed.length > 10
            ) {
              return trimmed.substring(0, 100);
            }
          }

          return message.substring(0, 100);
        };

        const formatRelativeTimestamp = (date: Date): string => {
          const diffMs = Date.now() - date.getTime();
          const diffMins = Math.floor(diffMs / 60000);
          const diffHours = Math.floor(diffMs / 3600000);
          const diffDays = Math.floor(diffMs / 86400000);

          if (diffMins < 1) return 'Just now';
          if (diffMins < 60) return `${diffMins}m ago`;
          if (diffHours < 24) return `${diffHours}h ago`;
          if (diffDays < 7) return `${diffDays}d ago`;

          return date.toLocaleDateString();
        };

        const formattedItems: HistoryItem[] = Array.isArray(sessions)
          ? sessions.map((session: any) => {
              const rawTimestamp =
                session.updated_at ||
                session.created_at ||
                new Date().toISOString();
              // new Date(...) never throws on bad input - it yields an
              // Invalid Date whose getTime() is NaN - so that is the check,
              // not try/catch. Fall back to the raw value for the visible
              // label and omit the tooltip rather than showing "Invalid Date".
              const date = new Date(rawTimestamp);
              const isValidDate = !Number.isNaN(date.getTime());

              return {
                id:
                  session.conversation_id ||
                  session.id ||
                  session.session_id ||
                  '',
                title: extractTitleFromMessage(session.last_message),
                timestamp: isValidDate
                  ? formatRelativeTimestamp(date)
                  : String(rawTimestamp),
                fullTimestamp: isValidDate ? date.toLocaleString() : undefined,
                type: session.task_id ? 'task' : 'chat',
                taskIcon: session.task_icon || undefined,
                taskName: session.task_name || undefined,
                taskId: session.task_id || undefined,
                webhookName: session.webhook_name || undefined,
              };
            })
          : [];

        setHistoryItems(formattedItems);
      } catch (error) {
        if (settings.devConsole) {
          console.error('[HistoryTab] Error fetching sessions:', error);
        }
        setHistoryItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessions();
  }, [selectedLoopId, teamspaceId, teamspaceName, aiService]);

  const handleHistoryItemClick = async (historyId: string, title: string) => {
    if (isBusy) return;

    // Find the history item to pass task info along
    const item = historyItems.find((h) => h.id === historyId);
    const taskInfo = item
      ? {
          type: item.type,
          taskIcon: item.taskIcon,
          taskName: item.taskName,
          taskId: item.taskId,
        }
      : undefined;

    const isDevelopment =
      process.env.NODE_ENV === 'development' ||
      process.env.REACT_APP_ENV === 'development';

    // Fetched concurrently, not sequentially — neither result depends on the
    // other, so awaiting one before starting the other only doubled the
    // wait on every history-item click for no benefit (found in review of
    // #989). Each still keeps its own independent try/catch (FR-014: a
    // messages failure must not skip the turns fetch, or vice versa), just
    // run via Promise.all instead of two sequential awaits.
    let messages: any = [];
    let turns: BackendTurn[] = [];
    if (!isDevelopment) {
      const fetchMessages = async () => {
        try {
          const response = await aiService.getSessionMessages(historyId);
          messages = response?.messages || response || [];
        } catch (error) {
          if (settings.devConsole) {
            console.error(
              '[HistoryTab] Error fetching session messages:',
              error,
            );
          }
          messages = [];
        }
      };
      const fetchTurns = async () => {
        try {
          const turnsResponse = await aiService.getConversationTurns(historyId);
          turns = turnsResponse?.turns || [];
        } catch (turnsError) {
          if (settings.devConsole) {
            console.error(
              '[HistoryTab] Error fetching conversation turns:',
              turnsError,
            );
          }
          turns = [];
        }
      };
      await Promise.all([fetchMessages(), fetchTurns()]);
    }

    onHistoryItemClick?.(historyId, title, messages, taskInfo, turns);
  };

  const handleDeleteClick = async (item: HistoryItem) => {
    if (!teamspaceId) return;
    try {
      await aiService.clearChat(
        item.id,
        user?.profile?.preferred_username ||
          user?.profile?.email?.split('@')[0] ||
          '',
        teamspaceId,
      );
      setHistoryItems((prev) => prev.filter((h) => h.id !== item.id));
    } catch (error) {
      if (settings.devConsole) {
        console.error('[HistoryTab] Error deleting session:', error);
      }
    }
  };

  return (
    <div className={cn('px-6 py-4', darkMode ? 'bg-[#1e1e1e]' : 'bg-white')}>
      <div className="mb-5">
        <h2
          className={cn(
            'mb-2 text-[18px] font-bold tracking-tight',
            darkMode ? 'text-[#e0e0e0]' : 'text-[#1a1a1a]',
          )}
        >
          {t('previous-chats') || 'Previous Chats'}
        </h2>
      </div>

      <div
        className={cn(
          'mt-5 flex max-h-[75vh] flex-col overflow-y-auto overflow-x-hidden pr-2',
          '[&::-webkit-scrollbar]:w-2',
          darkMode
            ? '[&::-webkit-scrollbar-thumb]:bg-[#555] [&::-webkit-scrollbar-track]:bg-[#2d2d2d]'
            : '[&::-webkit-scrollbar-thumb]:bg-[#c1c1c1] [&::-webkit-scrollbar-track]:bg-[#f1f1f1]',
        )}
      >
        {isLoading ? (
          <div className="flex h-[200px] items-center justify-center">
            <span
              role="progressbar"
              aria-label="loading"
              className={cn(
                'inline-block size-10 animate-spin rounded-full border-4 border-t-transparent',
                darkMode ? 'border-brand-blue' : 'border-brand-interactive',
              )}
            />
          </div>
        ) : historyItems.length > 0 ? (
          historyItems.map((item) => (
            <HistoryCard
              key={item.id}
              item={item}
              onHistoryItemClick={handleHistoryItemClick}
              onDeleteClick={handleDeleteClick}
              disabled={isBusy}
            />
          ))
        ) : (
          <p
            className={cn(
              'p-5 text-center text-sm',
              darkMode ? 'text-[#888]' : 'text-[#999]',
            )}
          >
            {t('no-history') || 'No chat history available'}
          </p>
        )}
      </div>
    </div>
  );
};

export default HistoryTab;
